// External libraries
import { ApolloLink } from 'apollo-link';
import { createHttpLink } from 'apollo-link-http';
import { BatchHttpLink } from 'apollo-link-batch-http';
import { isString } from '@checkout/react-components';

// Internal modules
import { getClientMetadataID } from '@/lib/utils';
import { getAccessToken, getGlobals, getLocale, getToken } from '@/lib/globals';

// Static headers - these will not change during the course of the transaction
const BASE_HEADERS = {
  'paypal-client-context': getToken(),
  'paypal-client-metadata-id': getClientMetadataID(),
  'x-app-name': 'checkoutuinodeweb_onboarding_lite',
};

// Replacing the trailing `/` so that the `graphql?<OPERATION_NAME>`
// will be shown in the network tab instead of just `<OPERATION_NAME>`
const BASE_URI = getGlobals().uri.queryEndpoint.replace(/\/$/, '');
const getDescriptiveUri = ({ isBatchedQuery, operationName, uri }) => {
  if (isBatchedQuery) {
    return `${BASE_URI}?BatchedQuery`;
  }

  return isString(uri) ? uri : `${BASE_URI}?${operationName}`;
};

// This link will dynamically set headers that may change during the
// course of the transaction. (e.g. locale headers, auth headers, etc.)
// It will also append the operation name as a query param to the URI so
// the entry in the network tab of the dev tools is easily identifiable.
const httpContextLink = new ApolloLink((operation, forward) => {
  const { country, locale } = getLocale();
  const { value: accessToken } = getAccessToken();
  const { operationName } = operation;

  operation.setContext(({ isBatchedQuery, headers, uri }) => ({
    headers: {
      ...BASE_HEADERS,

      // Headers passed as part of the context for the query/mutation
      ...headers,

      // Conditionally set auth token
      ...(accessToken && { 'x-paypal-internal-euat': accessToken }),

      // Used by xobuyernodeserv for locale resolution
      'x-country': country,
      'x-locale': locale,
    },

    // e.g. `https://www.paypal.com/graphql?SomeQuery`
    uri: getDescriptiveUri({ isBatchedQuery, operationName, uri }),
  }));

  return forward(operation);
});

const baseHttpLink = createHttpLink({
  credentials: 'include',
  uri: BASE_URI,
});

const batchHttpLink = new BatchHttpLink({
  credentials: 'include',
  uri: BASE_URI,
});

export const httpLink = httpContextLink.split(
  operation => !operation.getContext().isBatchedQuery,
  baseHttpLink,
  batchHttpLink,
);
